import { Box, Card, CardContent, Checkbox, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
    IconBan,
    IconBuildingWarehouse,
    IconCircleCheck,
    IconEdit,
    IconEye,
    IconPackage,
    IconPackages,
    IconQrcode,
    IconSquareX,
} from "@tabler/icons-react";
import type { FilaInventario, SeleccionInventario } from "../types/catalogoInventario.types";
import type { AccionesInventario, TextosInventario } from "./CatalogoInventarioColumnas";
import { COLORES_INVENTARIO } from "../constants/catalogoInventario.constants";
import { moneda, textoOVacio } from "../utils/catalogoInventarioFormato.utils";
import { esSeleccionableQr } from "../utils/qrProducto.utils";

interface Props {
    fila: FilaInventario;
    acciones: AccionesInventario;
    seleccion: SeleccionInventario;
    textos: Pick<TextosInventario, "ver" | "editar" | "activar" | "desactivar" | "yaTieneQr">;
    textoDesactivado: string;
    textoSinStock: string;
}

const estiloChip = {
    position: "absolute",
    top: 0,
    right: 0,
    p: 1,
    fontWeight: 500,
    borderRadius: "0 0 0 20px",
    background: COLORES_INVENTARIO.inactivo,
    maxWidth: 150,
    height: "auto",
    "& .MuiChip-label": { whiteSpace: "normal", lineHeight: "12px", color: COLORES_INVENTARIO.textoChip },
    "& .MuiChip-icon": { color: COLORES_INVENTARIO.textoChip },
} as const;

export const InventarioTarjeta = ({
    fila,
    acciones,
    seleccion,
    textos,
    textoDesactivado,
    textoSinStock,
}: Props): JSX.Element => {
    const seleccionado = seleccion.estaSeleccionado(fila.id);

    const chip = !fila.activo
        ? { icono: <IconBan size={18} />, texto: textoDesactivado }
        : fila.cantidad === 0
            ? { icono: <IconBan size={18} />, texto: textoSinStock }
            : { icono: <IconPackages size={18} />, texto: String(fila.cantidad) };

    const colorBorde = seleccionado
        ? COLORES_INVENTARIO.qr
        : fila.activo
            ? COLORES_INVENTARIO.activo
            : COLORES_INVENTARIO.inactivo;

    return (
        <Card
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                transition: "all 0.3s",
                border: `2px solid ${colorBorde}`,
                "&:hover": { boxShadow: fila.activo ? 6 : 2, transform: "translateY(-4px)" },
            }}
        >
            <Chip icon={chip.icono} label={chip.texto} sx={estiloChip} />

            {/* Encabezado: selección para QR + categoría */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: COLORES_INVENTARIO.primario, p: 1.5, pr: 10 }}>
                {fila.qr ? (
                    <Tooltip title={`${textos.yaTieneQr}: ${fila.qr.clave}`}>
                        <Box sx={{ display: "flex", color: COLORES_INVENTARIO.qr }}>
                            <IconQrcode stroke={2} />
                        </Box>
                    </Tooltip>
                ) : esSeleccionableQr(fila) ? (
                    <Checkbox
                        size="small"
                        checked={seleccionado}
                        onChange={() => seleccion.alternar(fila.id)}
                        inputProps={{ "aria-label": `Seleccionar ${fila.nombre}` }}
                        sx={{ p: 0, color: "#fff", "&.Mui-checked": { color: COLORES_INVENTARIO.qr } }}
                    />
                ) : (
                    <IconPackage stroke={2} color="#fff" />
                )}
                <Typography variant="button" noWrap sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
                    {textoOVacio(fila.categoria)}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
                {/* Acciones */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        p: 1.5,
                        bgcolor: "grey.50",
                        borderRight: "1px solid",
                        borderColor: "grey.200",
                    }}
                >
                    <Tooltip title={textos.ver}>
                        <IconButton size="small" onClick={() => acciones.onVer(fila)}>
                            <IconEye size={18} />
                        </IconButton>
                    </Tooltip>
                    {fila.activo && (
                        <Tooltip title={textos.editar}>
                            <IconButton size="small" color="warning" onClick={() => acciones.onEditar(fila)}>
                                <IconEdit size={18} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {fila.activo ? (
                        <Tooltip title={textos.desactivar}>
                            <IconButton size="small" color="error" onClick={() => acciones.onDesactivar(fila)}>
                                <IconSquareX size={20} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title={textos.activar}>
                            <IconButton size="small" color="success" onClick={() => acciones.onActivar(fila)}>
                                <IconCircleCheck size={20} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* Información */}
                <CardContent sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Typography variant="button" noWrap sx={{ fontWeight: 600 }} title={fila.nombre}>
                        {fila.nombre}
                    </Typography>
                    <Box sx={{ mt: "auto" }}>
                        <Typography variant="caption" display="block" noWrap>
                            <strong>Precio:</strong> {moneda(fila.precio)}
                        </Typography>
                        <Typography variant="caption" display="block" noWrap>
                            <strong>Clave:</strong> {textoOVacio(fila.clave)}
                        </Typography>
                        <Typography variant="caption" display="block" noWrap>
                            <strong>Ubicación:</strong> {textoOVacio(fila.ubicacion)}
                        </Typography>
                    </Box>
                </CardContent>
            </Box>

            {/* Pie: almacén */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    bgcolor: COLORES_INVENTARIO.primario,
                    color: "#fff",
                    py: 0.5,
                }}
            >
                <IconBuildingWarehouse stroke={2} size={18} />
                <Typography variant="button" noWrap sx={{ fontWeight: 600, color: "#fff" }}>
                    {textoOVacio(fila.almacen)}
                </Typography>
            </Box>
        </Card>
    );
};
